import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  X, CreditCard, Lock, MapPin, Check, Plus, Trash2, Loader2,
  AlertCircle, ShieldCheck, Calendar, ChevronLeft,
} from "lucide-react";
import { cn } from "../lib/utils";
import { DRAWER, getDrawerBackdropClass, getDrawerPanelClass } from "../lib/design-system";
import { Button } from "./ui";
import { DEFAULT_TAX_RATE } from "../constants/appConfig";
import {
  getPaymentMethods,
  attachPaymentMethod,
  deletePaymentMethod,
  type PaymentMethod,
} from "../services/payment-methods.api";
import { createPaymentIntent, confirmPayment } from "../services/payment.api";
import { GoogleAddressAutocomplete } from "./address/GoogleAddressAutocomplete";

// ============================================================================
// TYPES
// ============================================================================

interface PaymentDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  orderSummary: {
    productName: string;
    productPrice: number;
    tradeInValue?: number;
    rebateDiscount?: number;
    protectionPlan?: { name: string; price: number };
    shipping?: number;
    tax?: number;
  };
  onPaymentSuccess: (paymentDetails: PaymentDetails) => void;
}

export interface PaymentDetails {
  paymentMethodId?: string;
  cardLast4: string;
  cardBrand: string;
  cardholderName: string;
  billingAddress: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
  };
  paymentIntentId: string;
  amount: number;
}

// ============================================================================
// CARD VALIDATION
// ============================================================================

function luhnCheck(cardNumber: string): boolean {
  const digits = cardNumber.replace(/\D/g, "");
  if (digits.length < 13 || digits.length > 19) return false;
  let sum = 0;
  let alternate = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let n = parseInt(digits[i], 10);
    if (alternate) { n *= 2; if (n > 9) n -= 9; }
    sum += n;
    alternate = !alternate;
  }
  return sum % 10 === 0;
}

function detectCardBrand(cardNumber: string): string {
  const n = cardNumber.replace(/\D/g, "");
  if (/^4/.test(n)) return "visa";
  if (/^5[1-5]/.test(n) || /^2[2-7]/.test(n)) return "mastercard";
  if (/^3[47]/.test(n)) return "amex";
  if (/^6(?:011|5)/.test(n)) return "discover";
  return "card";
}

interface CardValidationResult {
  valid: boolean;
  errors: { field: string; message: string }[];
}

function validateCardForm(data: {
  cardNumber: string;
  cardholderName: string;
  expiryDate: string;
  cvv: string;
  zipCode?: string;
}): CardValidationResult {
  const errors: { field: string; message: string }[] = [];

  // Card number
  const raw = data.cardNumber.replace(/\D/g, "");
  if (!raw) errors.push({ field: "cardNumber", message: "Card number is required" });
  else if (raw.length < 13) errors.push({ field: "cardNumber", message: "Card number is too short" });
  else if (!luhnCheck(raw)) errors.push({ field: "cardNumber", message: "Invalid card number" });

  // Cardholder name
  if (!data.cardholderName.trim()) errors.push({ field: "cardholderName", message: "Cardholder name is required" });
  else if (data.cardholderName.trim().length < 2) errors.push({ field: "cardholderName", message: "Name is too short" });

  // Expiry
  const expRaw = data.expiryDate.replace(/\D/g, "");
  if (!expRaw) errors.push({ field: "expiryDate", message: "Expiry date is required" });
  else if (expRaw.length !== 4) errors.push({ field: "expiryDate", message: "Enter a valid expiry (MM/YY)" });
  else {
    const month = parseInt(expRaw.substring(0, 2), 10);
    const year = parseInt("20" + expRaw.substring(2, 4), 10);
    if (month < 1 || month > 12) errors.push({ field: "expiryDate", message: "Invalid month" });
    else {
      const now = new Date();
      const expiryDate = new Date(year, month, 1);
      if (expiryDate <= now) errors.push({ field: "expiryDate", message: "Card has expired" });
    }
  }

  // CVV
  const cvvRaw = data.cvv.replace(/\D/g, "");
  if (!cvvRaw) errors.push({ field: "cvv", message: "CVV is required" });
  else if (cvvRaw.length < 3) errors.push({ field: "cvv", message: "CVV must be 3-4 digits" });

  // ZIP (optional but validated if provided)
  const zip = (data.zipCode || "").replace(/\D/g, "");
  if (zip && zip.length !== 5) errors.push({ field: "zipCode", message: "ZIP must be 5 digits" });

  return { valid: errors.length === 0, errors };
}

// ============================================================================
// COMPONENT
// ============================================================================

type FlowStep = "select-card" | "add-card" | "billing" | "review" | "processing" | "success" | "error";

export function PaymentDrawer({
  isOpen, onClose, orderSummary, onPaymentSuccess,
}: Readonly<PaymentDrawerProps>) {
  const { t } = useTranslation("payment");

  // Flow state
  const [step, setStep] = useState<FlowStep>("select-card");
  const [savedCards, setSavedCards] = useState<PaymentMethod[]>([]);
  const [loadingCards, setLoadingCards] = useState(true);

  // Card selection
  const [selectedCard, setSelectedCard] = useState<PaymentMethod | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);

  // Card form
  const [cardNumber, setCardNumber] = useState("");
  const [cardholderName, setCardholderName] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [cvv, setCvv] = useState("");
  const [saveCard, setSaveCard] = useState(true);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Billing
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");

  // Processing
  const [processing, setProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Success details
  const [successDetails, setSuccessDetails] = useState<PaymentDetails | null>(null);

  // Computed
  const subtotal = orderSummary.productPrice;
  const tradeIn = orderSummary.tradeInValue || 0;
  const rebate = orderSummary.rebateDiscount || 0;
  const protectionPlan = orderSummary.protectionPlan?.price || 0;
  const shipping = orderSummary.shipping || 0;
  const tax = orderSummary.tax ?? Math.round((subtotal - tradeIn - rebate + protectionPlan + shipping) * DEFAULT_TAX_RATE * 100) / 100;
  const total = Math.max(0, subtotal - tradeIn - rebate + protectionPlan + shipping + tax);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  useEffect(() => {
    if (isOpen) {
      loadSavedCards();
      resetForm();
    }
  }, [isOpen]);

  const loadSavedCards = async () => {
    try {
      setLoadingCards(true);
      const cards = await getPaymentMethods();
      setSavedCards(cards);
      const defaultCard = cards.find((c) => c.isDefault);
      if (defaultCard) setSelectedCard(defaultCard);
      if (cards.length === 0) {
        setIsAddingNew(true);
        setStep("add-card");
      } else {
        setStep("select-card");
      }
    } catch {
      setIsAddingNew(true);
      setStep("add-card");
    } finally {
      setLoadingCards(false);
    }
  };

  const resetForm = () => {
    setStep("select-card");
    setIsAddingNew(false);
    setSelectedCard(null);
    setCardNumber(""); setCardholderName(""); setExpiryDate(""); setCvv("");
    setSaveCard(true); setFormErrors({});
    setStreet(""); setCity(""); setState(""); setZipCode("");
    setProcessing(false); setErrorMessage(""); setSuccessDetails(null);
  };

  // ============================================================================
  // HELPERS
  // ============================================================================

  const formatCardNumber = (v: string) => {
    const c = v.replace(/\s/g, "");
    return (c.match(/.{1,4}/g)?.join(" ") ?? c).substring(0, 19);
  };

  const formatExpiryDate = (v: string) => {
    const c = v.replace(/\D/g, "");
    if (c.length >= 2) {
      const m = parseInt(c.substring(0, 2), 10);
      if (m > 12) return c.substring(0, 1);
    }
    return c.length >= 2 ? `${c.substring(0, 2)}/${c.substring(2, 4)}` : c;
  };

  const handleDeleteCard = async (cardId: string) => {
    try {
      await deletePaymentMethod(cardId);
      await loadSavedCards();
    } catch { /* ignore */ }
  };

  const clearFieldError = (field: string) => {
    if (formErrors[field]) setFormErrors((p) => { const n = { ...p }; delete n[field]; return n; });
  };

  // ============================================================================
  // STEP HANDLERS
  // ============================================================================

  const handleGoToAddCard = () => {
    setIsAddingNew(true);
    setCardNumber(""); setCardholderName(""); setExpiryDate(""); setCvv("");
    setFormErrors({});
    setStep("add-card");
  };

  const handleAddCardContinue = () => {
    const result = validateCardForm({ cardNumber, cardholderName, expiryDate, cvv });
    if (!result.valid) {
      const errObj: Record<string, string> = {};
      result.errors.forEach((e) => { errObj[e.field] = e.message; });
      setFormErrors(errObj);
      return;
    }
    setFormErrors({});
    setSelectedCard(null);
    setStep("billing");
  };

  const handleBillingContinue = () => {
    const errors: Record<string, string> = {};
    if (!street.trim()) errors.street = "Street address is required";
    if (!city.trim()) errors.city = "City is required";
    if (!state.trim()) errors.state = "State is required";
    if (!zipCode.trim() || zipCode.replace(/\D/g, "").length !== 5) errors.zipCode = "Valid 5-digit ZIP is required";
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    setFormErrors({});
    setStep("review");
  };

  const handleContinueToReview = () => {
    if (selectedCard) {
      setStep("review");
    } else {
      handleAddCardContinue();
    }
  };

  // ============================================================================
  // PAYMENT PROCESSING
  // ============================================================================

  const handleCompletePayment = async () => {
    setProcessing(true);
    setErrorMessage("");
    setStep("processing");

    try {
      // Step 1: Create payment intent
      setProcessingStep("Creating payment...");
      const { clientSecret, paymentIntentId } = await createPaymentIntent({
        amount: Math.round(total * 100),
        currency: "usd",
        metadata: { productName: orderSummary.productName },
      });

      // Step 2: Process payment (simulate Stripe tokenization + confirmation)
      setProcessingStep("Processing payment...");
      await new Promise((r) => setTimeout(r, 1500));

      // Step 3: Confirm payment with backend
      setProcessingStep("Confirming payment...");
      const cardBrand = selectedCard ? selectedCard.brand : detectCardBrand(cardNumber);
      const last4 = selectedCard ? selectedCard.last4 : cardNumber.replace(/\D/g, "").slice(-4);
      const holderName = selectedCard?.billingName ?? cardholderName;

      const result = await confirmPayment(paymentIntentId);

      if (result.status !== "succeeded") {
        throw new Error("Payment was not successful. Please try again.");
      }

      // Step 4: Save card if needed
      if (isAddingNew && saveCard) {
        try {
          const [month, year] = expiryDate.split("/");
          await attachPaymentMethod({
            stripePaymentMethodId: `pm_${Date.now()}`,
            brand: cardBrand,
            last4,
            expMonth: parseInt(month, 10),
            expYear: parseInt(`20${year}`, 10),
            billingName: holderName,
            isDefault: savedCards.length === 0,
          });
        } catch { /* non-fatal */ }
      }

      // Success
      const details: PaymentDetails = {
        cardLast4: last4,
        cardBrand,
        cardholderName: holderName,
        billingAddress: { street, city, state, zipCode },
        paymentIntentId,
        amount: total,
      };
      setSuccessDetails(details);
      setStep("success");

      setTimeout(() => {
        onPaymentSuccess(details);
        resetForm();
      }, 2500);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "Payment failed. Please try again.");
      setStep("error");
    } finally {
      setProcessing(false);
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!isOpen) return null;

  const inputClass = "w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-ring outline-none transition-colors bg-background";
  const errorInputClass = "border-red-500 focus:ring-red-200";

  return (
    <>
      {/* Backdrop */}
      <button type="button" className={getDrawerBackdropClass(true)} onClick={onClose} aria-label="Close" />

      {/* Panel */}
      <div className={getDrawerPanelClass(true, "xl")}>
        {/* Header */}
        <div className={DRAWER.header.container}>
          <div className="flex items-center gap-3">
            {step !== "success" && step !== "processing" && step !== "error" && (
              <button onClick={step === "select-card" ? onClose : (() => { 
                if (step === "add-card" && savedCards.length > 0) { setIsAddingNew(false); setStep("select-card"); setFormErrors({}); }
                else if (step === "billing") setStep("add-card");
                else if (step === "review") setStep(isAddingNew ? "billing" : "select-card");
              })} className="h-8 w-8 rounded-lg hover:bg-secondary flex items-center justify-center transition-colors">
                <ChevronLeft className="h-5 w-5" />
              </button>
            )}
            <div>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                {step === "success" ? <Check className="h-4 w-4 text-green-500" />
                 : step === "processing" ? <Loader2 className="h-4 w-4 animate-spin" />
                 : step === "error" ? <AlertCircle className="h-4 w-4 text-red-500" />
                 : <Lock className="h-4 w-4" />}
                {step === "success" ? "Payment Complete"
                 : step === "processing" ? "Processing Payment"
                 : step === "error" ? "Payment Failed"
                 : t("drawer.title")}
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {step === "success" ? "Your payment has been processed successfully"
                 : step === "processing" ? processingStep
                 : step === "error" ? "There was an issue with your payment"
                 : t("drawer.encryptedSecure")}
              </p>
            </div>
          </div>
          {step !== "processing" && (
            <button onClick={onClose} className="h-8 w-8 rounded-lg hover:bg-secondary flex items-center justify-center transition-colors">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Body */}
        <div className={DRAWER.body}>
          {/* Order Summary - shown on all steps */}
          <div className="rounded-lg border p-4 mb-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">{t("drawer.orderSummary")}</p>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground truncate mr-2">{orderSummary.productName}</span><span className="tabular-nums">${subtotal.toFixed(2)}</span></div>
              {tradeIn > 0 && <div className="flex justify-between text-green-600"><span className="text-muted-foreground">{t("drawer.tradeIn")}</span><span className="tabular-nums">-${tradeIn.toFixed(2)}</span></div>}
              {rebate > 0 && <div className="flex justify-between text-green-600"><span className="text-muted-foreground">{t("drawer.rebate")}</span><span className="tabular-nums">-${rebate.toFixed(2)}</span></div>}
              {protectionPlan > 0 && <div className="flex justify-between"><span className="text-muted-foreground">{orderSummary.protectionPlan?.name}</span><span className="tabular-nums">${protectionPlan.toFixed(2)}</span></div>}
              {shipping > 0 && <div className="flex justify-between"><span className="text-muted-foreground">{t("drawer.shipping")}</span><span className="tabular-nums">${shipping.toFixed(2)}</span></div>}
              <div className="flex justify-between"><span className="text-muted-foreground">{t("drawer.tax")}</span><span className="tabular-nums">${tax.toFixed(2)}</span></div>
              <div className="flex justify-between pt-2 border-t font-semibold"><span>{t("drawer.total")}</span><span className="tabular-nums">${total.toFixed(2)}</span></div>
            </div>
          </div>

          {/* â”€â”€ PROCESSING â”€â”€ */}
          {step === "processing" && (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <div className="h-16 w-16 rounded-full bg-blue-50 flex items-center justify-center">
                <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
              </div>
              <p className="text-sm font-medium text-foreground">{processingStep}</p>
              <p className="text-xs text-muted-foreground">This may take a moment. Please do not close this window.</p>
            </div>
          )}

          {/* â”€â”€ SUCCESS â”€â”€ */}
          {step === "success" && successDetails && (
            <div className="flex flex-col items-center py-8 gap-4">
              <div className="h-20 w-20 rounded-full bg-green-100 flex items-center justify-center">
                <Check className="h-10 w-10 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">{t("drawer.paymentSuccessful")}</h3>
              <div className="w-full rounded-lg bg-secondary/20 border p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-semibold capitalize">{successDetails.cardBrand} â€¢â€¢â€¢â€¢ {successDetails.cardLast4}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">{successDetails.billingAddress.street}, {successDetails.billingAddress.city}, {successDetails.billingAddress.state} {successDetails.billingAddress.zipCode}</span>
                </div>
                <div className="flex justify-between pt-2 border-t">
                  <span className="text-xs text-muted-foreground">{t("drawer.amountCharged")}</span>
                  <span className="text-sm font-semibold tabular-nums">${successDetails.amount.toFixed(2)}</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground text-center">{t("drawer.completingShortly")}</p>
            </div>
          )}

          {/* â”€â”€ ERROR â”€â”€ */}
          {step === "error" && (
            <div className="flex flex-col items-center py-8 gap-4">
              <div className="h-20 w-20 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="h-10 w-10 text-red-500" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">Payment Failed</h3>
              <p className="text-sm text-muted-foreground text-center max-w-sm">{errorMessage}</p>
              <div className="flex gap-3 mt-2">
                <Button variant="outline" onClick={() => setStep("review")}>Try Again</Button>
                <Button variant="outline" onClick={resetForm}>Start Over</Button>
              </div>
            </div>
          )}

          {/* â”€â”€ SELECT CARD â”€â”€ */}
          {step === "select-card" && !loadingCards && (
            <div className="space-y-4">
              <p className="text-sm font-semibold">{t("drawer.paymentMethod")}</p>
              {savedCards.map((card) => (
                <button key={card.id} onClick={() => setSelectedCard(card)}
                  className={cn("w-full rounded-lg border p-3 text-left transition-colors", selectedCard?.id === card.id ? "border-foreground bg-secondary/40" : "hover:bg-secondary/20")}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CreditCard className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold capitalize">{card.brand}</span>
                          <span className="text-sm text-muted-foreground font-mono">â€¢â€¢â€¢â€¢ {card.last4}</span>
                          {card.isDefault && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-secondary">{t("drawer.defaultBadge")}</span>}
                        </div>
                        <p className="text-xs text-muted-foreground">{t("drawer.expiresPrefix")} {card.expMonth.toString().padStart(2, "0")}/{card.expYear.toString().slice(-2)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {selectedCard?.id === card.id && <Check className="h-4 w-4" />}
                      {!card.isDefault && (
                        <button onClick={(e) => { e.stopPropagation(); handleDeleteCard(card.id); }} className="h-7 w-7 rounded-lg hover:bg-secondary flex items-center justify-center">
                          <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                        </button>
                      )}
                    </div>
                  </div>
                </button>
              ))}
              <button onClick={handleGoToAddCard} className="w-full rounded-lg border border-dashed p-3 hover:bg-secondary/20 transition-colors flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Plus className="h-4 w-4" /> {t("drawer.addNewCard")}
              </button>
              {selectedCard && (
                <Button onClick={handleContinueToReview} className="w-full">{t("drawer.continueWithCard", { last4: selectedCard.last4 })}</Button>
              )}
            </div>
          )}

          {/* â”€â”€ ADD CARD â”€â”€ */}
          {step === "add-card" && (
            <div className="space-y-4">
              <p className="text-sm font-semibold">{t("drawer.cardDetails")}</p>
              {savedCards.length > 0 && (
                <Button variant="outline" size="sm" onClick={() => { setIsAddingNew(false); setStep("select-card"); setFormErrors({}); }}>{t("drawer.cancel")}</Button>
              )}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium mb-1.5">{t("drawer.cardholderName")}</label>
                  <input type="text" value={cardholderName} onChange={(e) => { setCardholderName(e.target.value); clearFieldError("cardholderName"); }}
                    placeholder={t("drawer.cardholderPlaceholder")}
                    className={cn(inputClass, formErrors.cardholderName && errorInputClass)} />
                  {formErrors.cardholderName && <p className="text-xs text-red-500 mt-1">{formErrors.cardholderName}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5">{t("drawer.cardNumber")}</label>
                  <input type="text" value={cardNumber} onChange={(e) => { setCardNumber(formatCardNumber(e.target.value)); clearFieldError("cardNumber"); }}
                    placeholder={t("drawer.cardNumberPlaceholder")} maxLength={19} inputMode="numeric" autoComplete="cc-number"
                    className={cn(inputClass, "font-mono", formErrors.cardNumber && errorInputClass)} />
                  {formErrors.cardNumber && <p className="text-xs text-red-500 mt-1">{formErrors.cardNumber}</p>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1.5">{t("drawer.expiryLabel")}</label>
                    <input type="text" value={expiryDate} onChange={(e) => { setExpiryDate(formatExpiryDate(e.target.value)); clearFieldError("expiryDate"); }}
                      placeholder={t("drawer.expiryPlaceholder")} maxLength={5} inputMode="numeric" autoComplete="cc-exp"
                      className={cn(inputClass, "font-mono", formErrors.expiryDate && errorInputClass)} />
                    {formErrors.expiryDate && <p className="text-xs text-red-500 mt-1">{formErrors.expiryDate}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1.5">{t("drawer.cvvLabel")}</label>
                    <input type="text" value={cvv} onChange={(e) => { setCvv(e.target.value.replace(/\D/g, "").substring(0, 4)); clearFieldError("cvv"); }}
                      placeholder="123" maxLength={4} inputMode="numeric" autoComplete="cc-csc"
                      className={cn(inputClass, "font-mono", formErrors.cvv && errorInputClass)} />
                    {formErrors.cvv && <p className="text-xs text-red-500 mt-1">{formErrors.cvv}</p>}
                  </div>
                </div>
                <label className="flex items-center gap-2 p-3 rounded-lg border cursor-pointer text-sm">
                  <input type="checkbox" checked={saveCard} onChange={(e) => setSaveCard(e.target.checked)} className="h-4 w-4 rounded" />
                  {t("drawer.saveForFuture")}
                </label>
                <Button onClick={handleAddCardContinue} disabled={!cardNumber || !cardholderName || !expiryDate || !cvv} className="w-full">{t("drawer.continue")}</Button>
              </div>
            </div>
          )}

          {/* â”€â”€ BILLING â”€â”€ */}
          {step === "billing" && (
            <div className="space-y-4">
              <p className="text-sm font-semibold flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {t("drawer.billingAddress")}</p>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium mb-1.5">{t("drawer.street")}</label>
                  <GoogleAddressAutocomplete id="billing-street" value={street} onChange={(v) => { setStreet(v); clearFieldError("street"); }}
                    onPlaceSelected={(p) => { if (p.city) setCity(p.city); if (p.state) setState(p.state); if (p.zipCode) setZipCode(p.zipCode.replace(/\D/g, "").substring(0, 5)); clearFieldError("zipCode"); }}
                    placeholder={t("drawer.streetPlaceholder")}
                    className={cn(inputClass, formErrors.street && errorInputClass)} />
                  {formErrors.street && <p className="text-xs text-red-500 mt-1">{formErrors.street}</p>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1.5">{t("drawer.city")}</label>
                    <input type="text" value={city} onChange={(e) => { setCity(e.target.value); clearFieldError("city"); }}
                      placeholder={t("drawer.cityPlaceholder")} className={cn(inputClass, formErrors.city && errorInputClass)} />
                    {formErrors.city && <p className="text-xs text-red-500 mt-1">{formErrors.city}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1.5">{t("drawer.state")}</label>
                    <input type="text" value={state} onChange={(e) => { setState(e.target.value); clearFieldError("state"); }}
                      placeholder={t("drawer.statePlaceholder")} className={cn(inputClass, formErrors.state && errorInputClass)} />
                    {formErrors.state && <p className="text-xs text-red-500 mt-1">{formErrors.state}</p>}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5">{t("drawer.zipCode")}</label>
                  <input type="text" value={zipCode} onChange={(e) => { setZipCode(e.target.value.replace(/\D/g, "").substring(0, 5)); clearFieldError("zipCode"); }}
                    placeholder="12345" maxLength={5} inputMode="numeric" autoComplete="postal-code"
                    className={cn(inputClass, formErrors.zipCode && errorInputClass)} />
                  {formErrors.zipCode && <p className="text-xs text-red-500 mt-1">{formErrors.zipCode}</p>}
                </div>
                <div className="flex gap-2 pt-1">
                  <Button variant="outline" onClick={() => setStep("add-card")} className="flex-1">{t("drawer.back")}</Button>
                  <Button onClick={handleBillingContinue} disabled={!street || !city || !state || !zipCode} className="flex-1">{t("drawer.review")}</Button>
                </div>
              </div>
            </div>
          )}

          {/* â”€â”€ REVIEW â”€â”€ */}
          {step === "review" && (
            <div className="space-y-4">
              <p className="text-sm font-semibold flex items-center gap-1.5"><Check className="h-4 w-4" /> {t("drawer.reviewConfirm")}</p>

              <div className="rounded-lg border p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">{t("drawer.paymentMethod")}</p>
                {selectedCard ? (
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-semibold capitalize">{selectedCard.brand} â€¢â€¢â€¢â€¢ {selectedCard.last4}</span>
                    <span className="text-xs text-muted-foreground">{selectedCard.expMonth.toString().padStart(2, "0")}/{selectedCard.expYear.toString().slice(-2)}</span>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm font-semibold">â€¢â€¢â€¢â€¢ {cardNumber.replace(/\D/g, "").slice(-4)}</p>
                    <p className="text-xs text-muted-foreground">{cardholderName} Â· {expiryDate}</p>
                  </div>
                )}
              </div>

              {street && (
                <div className="rounded-lg border p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">{t("drawer.billingAddress")}</p>
                  <p className="text-sm">{street}</p>
                  <p className="text-xs text-muted-foreground">{city}, {state} {zipCode}</p>
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={() => setStep(isAddingNew ? "billing" : "select-card")} className="flex-1">{t("drawer.back")}</Button>
                <Button onClick={handleCompletePayment} disabled={processing} className="flex-1">
                  {processing ? <span className="flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("drawer.processing")}</span>
                   : <span className="flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> {t("drawer.payAmount", { amount: total.toFixed(2) })}</span>}
                </Button>
              </div>
            </div>
          )}

          {loadingCards && (
            <div className="flex flex-col items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground mb-2" />
              <p className="text-xs text-muted-foreground">{t("drawer.loadingMethods")}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={DRAWER.footer}>
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <ShieldCheck className="h-3 w-3" /> {t("drawer.sslEncrypted")}
          </div>
        </div>
      </div>
    </>
  );
}